import { useContext, useMemo } from 'react';

import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { EMBRYO_METHOD_OPTIONS, SEX_EMBRYO } from '@/constants';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { utcDateFormatter } from '@/grid/value-formatters/utcInstantFormatter';
import { PrimaryContext } from '@/services';
import { useGetEmbryoCalves } from '@/services/embryo-service/hooks';
import { useContextMenu } from '@/utils/context-menu';

interface EmbryoCalvesTabProps {
  embryoIds: string[] | undefined;
}

export function EmbryoCalvesTab({ embryoIds }: EmbryoCalvesTabProps) {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const { data: calves, isLoading } = useGetEmbryoCalves(embryoIds);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'tag',
        headerName: t('embryo.calvesTag'),
        filter: 'agTextColumnFilter',
        width: 100,
      },
      {
        field: 'ownerName',
        headerName: t('embryo.calvesOwner'),
        filter: 'agTextColumnFilter',
        width: 120,
      },
      {
        field: 'donorName',
        headerName: t('embryo.calvesDonor'),
        filter: 'agTextColumnFilter',
        width: 120,
      },
      {
        field: 'recipientTag',
        headerName: t('embryo.calvesRecipient'),
        filter: 'agTextColumnFilter',
        width: 100,
      },
      {
        field: 'statusName',
        headerName: t('embryo.calvesStatus'),
        filter: 'agSetColumnFilter',
        width: 100,
      },
      {
        field: 'yardName',
        headerName: t('embryo.calvesYard'),
        filter: 'agTextColumnFilter',
        width: 120,
      },
      {
        field: 'penName',
        headerName: t('embryo.calvesPen'),
        filter: 'agTextColumnFilter',
        width: 100,
      },
      {
        field: 'method',
        headerName: t('embryo.calvesMethod'),
        filter: 'agTextColumnFilter',
        width: 100,
        valueFormatter: (params) =>
          EMBRYO_METHOD_OPTIONS.find(
            (opt) => opt.value === Number(params.value),
          )?.label ?? '',
      },
      {
        field: 'tattoo',
        headerName: t('embryo.calvesTattoo'),
        filter: 'agTextColumnFilter',
        width: 80,
      },
      {
        field: 'sex',
        headerName: t('embryo.calvesSex'),
        filter: 'agSetColumnFilter',
        width: 80,
        valueFormatter: (params) =>
          Object.entries(SEX_EMBRYO).find(
            ([, val]) => val === Number(params.value),
          )?.[0] ?? '',
      },
      {
        field: 'birth',
        headerName: t('embryo.calvesBirthDate'),
        filter: 'agDateColumnFilter',
        valueFormatter: dateOnlyFormatter,
        width: 100,
      },
      {
        field: 'ship',
        headerName: t('embryo.calvesShipDate'),
        filter: 'agDateColumnFilter',
        valueFormatter: dateOnlyFormatter,
        width: 100,
      },
      {
        field: 'createdOn',
        headerName: t('embryo.calvesCreatedOn'),
        filter: 'agDateColumnFilter',
        valueFormatter: utcDateFormatter,
        width: 100,
      },
    ],
    [t],
  );

  return (
    <div className="calves-tab">
      <div className="ag-grid-height-300">
        <WithLocalStorage storageKey="embryo-calves-grid">
          <AgGridReact
            gridOptions={defaultGridOptions}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
            defaultColDef={defaultColDef}
            rowData={calves ?? []}
            columnDefs={columnDefs}
            loading={isLoading}
            getContextMenuItems={getContextMenuItems}
          />
        </WithLocalStorage>
      </div>
    </div>
  );
}
